import { useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  computePerLayerComponents,
  formatUsesHint,
  getScaledFrostingStructureFromBlock,
  suggestTierBaseWeights,
  sumComponents,
} from "@/lib/cakeFrostingStructures";
import {
  bumpCalculatorWeight,
  formatCalculatorWeight,
} from "@/lib/calculatorWeight";

/** Whole grams only on froster screens (63.75 → 64). */
function fmtGrams(value) {
  if (value == null || !Number.isFinite(Number(value))) return "—";
  return `${Math.round(Number(value))}`;
}

/** Left / right step buttons (no browser spinners). */
function FrosterStepperInput({ value, onChange, min, max, step = 1, decimals = 0, calculatorWeight = false }) {
  const num = Number(value);
  const current = Number.isFinite(num) ? num : min;

  const format = (n) => {
    if (calculatorWeight) return formatCalculatorWeight(n);
    if (decimals > 0) return String(Math.round(n * 100) / 100);
    return String(Math.round(n));
  };

  const bump = (direction) => {
    let next;
    if (calculatorWeight) {
      next = bumpCalculatorWeight(current, direction);
    } else {
      next = current + direction * step;
      next = Math.round(next / step) * step;
      next = Math.max(min, Math.min(max, next));
    }
    onChange(format(next));
  };

  return (
    <div className="driver-frost-stepper">
      <button
        type="button"
        className="driver-frost-stepper-btn"
        onClick={() => bump(-1)}
        aria-label="Decrease"
      >
        −
      </button>
      <input
        type="text"
        inputMode={decimals > 0 ? "decimal" : "numeric"}
        className="driver-frost-stepper-value"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        readOnly
        aria-readonly="true"
      />
      <button
        type="button"
        className="driver-frost-stepper-btn driver-frost-stepper-btn--plus"
        onClick={() => bump(1)}
        aria-label="Increase"
      >
        +
      </button>
    </div>
  );
}

function resolveBasicComponents(block, scaled) {
  if (block.base_components && Object.keys(block.base_components).length > 0) {
    return block.base_components;
  }
  const mult = Math.max(Number(block.weight_kg) || 0.5, 0.25) / 0.5;
  const out = {};
  for (const [code, grams] of Object.entries(scaled || {})) {
    const g = Number(grams);
    if (Number.isFinite(g)) out[code] = Math.round((g / mult) * 100) / 100;
  }
  return out;
}

function orderedCodes(basic, scaled) {
  const set = new Set([...Object.keys(basic || {}), ...Object.keys(scaled || {})]);
  const order = ["B", "S", "WC", "CC", "F", "T", "DW", "DF", "DCC", "DT"];
  return [...order.filter((c) => set.has(c)), ...[...set].filter((c) => !order.includes(c))];
}

/** Froster view: horizontal table — codes as columns, Scaled + Layer as rows. */
function FrosterWorkTable({ scaled, layerComponents }) {
  const codes = useMemo(
    () => orderedCodes({}, { ...scaled, ...layerComponents }),
    [scaled, layerComponents]
  );
  const scaledTotal = sumComponents(scaled);
  const layerTotal = sumComponents(layerComponents);

  return (
    <div className="driver-frost-table-h-scroll">
      <table className="driver-frost-table--work driver-frost-table--horizontal">
        <thead>
          <tr>
            <th className="row-label" scope="row" />
            {codes.map((code) => (
              <th key={code} className="code-col" scope="col">
                {code}
              </th>
            ))}
            <th className="code-col total-col" scope="col">
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          <tr className="total-data-row">
            <th className="row-label row-label--sm" scope="row">
              Total
            </th>
            {codes.map((code) => (
              <td key={`s-${code}`} className="num">
                {scaled[code] != null && Number.isFinite(Number(scaled[code]))
                  ? fmtGrams(scaled[code])
                  : "—"}
              </td>
            ))}
            <td className="num total-col">{fmtGrams(scaledTotal)}</td>
          </tr>
          <tr className="layer-row">
            <th className="row-label row-label--sm" scope="row">
              Layer
            </th>
            {codes.map((code) => (
              <td key={`l-${code}`} className="layer">
                {layerComponents[code] != null && Number.isFinite(Number(layerComponents[code]))
                  ? fmtGrams(layerComponents[code])
                  : "—"}
              </td>
            ))}
            <td className="layer total-col">{fmtGrams(layerTotal)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/** 0.5 kg reference — horizontal (codes as columns). */
function FrostingIngredientsBar({ items }) {
  if (!items?.length) return null;
  return (
    <div className="driver-frost-ingredients-bar">
      <div className="driver-label">Frosting ingredients</div>
      <p className="driver-frost-ingredients-names">
        {items.map((item) => item.name).filter(Boolean).join(" · ")}
      </p>
    </div>
  );
}

/** Cheese cake: top filling only — no B/S/WC structure table. */
function CheesecakeTopFillingPanel({ block }) {
  const items = block.frosting_ingredients || [];
  return (
    <div className="space-y-3 px-3 pb-3">
      <p className="text-sm font-bold text-[#2D2D2D]">
        Top filling only (after bake &amp; cool)
      </p>
      <p className="text-xs text-[#666]">
        No cake-base frosting layers — scale with order size (×2 / ×3 / ×4 from 260g recipe).
      </p>
      {items.length > 0 ? (
        <div className="overflow-hidden rounded-xl border border-[#EBB3A9]/45">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#EEEBD0]/60 text-left text-xs uppercase tracking-wide text-[#2D2D2D]/70">
                <th className="px-3 py-2">Ingredient</th>
                <th className="px-3 py-2 w-[40%]">Amount</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={`${item.name}-${idx}`} className="border-t border-[#EBB3A9]/25">
                  <td className="px-3 py-2 font-medium text-[#2D2D2D]">{item.name}</td>
                  <td className="px-3 py-2 text-[#555]">{item.amount || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-[#888] italic">No top filling lines on this snapshot.</p>
      )}
    </div>
  );
}

function BasicStructureBar({ basic }) {
  const codes = useMemo(() => orderedCodes(basic, {}), [basic]);
  const basicTotal = sumComponents(basic);

  return (
    <div className="driver-frost-basic-bar">
      <div className="driver-label">Basic structure (0.5 kg)</div>
      <div className="driver-frost-table-h-scroll">
        <table className="driver-frost-table--basic driver-frost-table--horizontal">
          <thead>
            <tr>
              <th className="row-label" scope="row" />
              {codes.map((code) => (
                <th key={code} className="code-col" scope="col">
                  {code}
                </th>
              ))}
              <th className="code-col total-col" scope="col">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className="basic-data-row">
              <th className="row-label row-label--sm" scope="row">
                Basic
              </th>
              {codes.map((code) => (
                <td key={code} className="num">
                  {basic[code] != null && Number.isFinite(Number(basic[code]))
                    ? fmtGrams(basic[code])
                    : "—"}
                </td>
              ))}
              <td className="num total-col">{fmtGrams(basicTotal)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FrosterBasePanel({
  block,
  positionLabel,
  weightKg,
  layers,
  onWeightChange,
  onLayersChange,
  showWeightInput,
  onRemove,
  weightMin = 0.25,
  weightStep = 0.25,
  weightDecimals = 2,
  calculatorWeight = false,
}) {
  const flavor = block.flavor || block.label?.split(" (")[0] || "";
  const tierStruct = useMemo(
    () => getScaledFrostingStructureFromBlock(block, weightKg),
    [block, weightKg]
  );
  const scaled = showWeightInput
    ? tierStruct?.components || {}
    : block.components || tierStruct?.components || {};
  const layerComponents = useMemo(
    () =>
      computePerLayerComponents(scaled, block.structure_family, block.kind, layers),
    [scaled, block.structure_family, block.kind, layers]
  );

  return (
    <div className="driver-frost-base-panel">
      <div className={`driver-frost-base-head${onRemove ? " driver-frost-base-head--toolbar" : ""}`}>
        <span>{positionLabel}</span>
        {onRemove ? (
          <button type="button" className="driver-calc-remove-base" onClick={onRemove}>
            Remove
          </button>
        ) : null}
      </div>
      <div className="driver-frost-base-inputs">
        {showWeightInput ? (
          <div>
            <div className="driver-input-label">Weight (kg)</div>
            <FrosterStepperInput
              value={weightKg}
              onChange={onWeightChange}
              min={weightMin}
              max={20}
              step={weightStep}
              decimals={weightDecimals}
              calculatorWeight={calculatorWeight}
            />
          </div>
        ) : null}
        <div style={showWeightInput ? undefined : { gridColumn: "1 / -1" }}>
          <div className="driver-input-label">Layers (L)</div>
          <FrosterStepperInput
            value={layers}
            onChange={onLayersChange}
            min={1}
            max={20}
            step={1}
          />
        </div>
      </div>
      <div className="driver-frost-base-table-wrap">
        <FrosterWorkTable scaled={scaled} layerComponents={layerComponents} />
      </div>
    </div>
  );
}

function tierPositionLabel(displayIndex) {
  return `Base ${displayIndex + 1}`;
}

function TierCakeFrosterLayout({ block }) {
  const tierCount = block.tier_base_count || (Number(block.tiers) || 0) + 1;
  const totalKg = Number(block.weight_kg) || 0.5;
  const basic = useMemo(
    () => resolveBasicComponents(block, block.components),
    [block]
  );
  const suggested = useMemo(
    () => suggestTierBaseWeights(totalKg, tierCount),
    [totalKg, tierCount]
  );

  const [tierInputs, setTierInputs] = useState(() =>
    suggested.map((w) => ({ weightKg: String(w), layers: "4" }))
  );

  const updateTier = (storageIndex, field, value) => {
    setTierInputs((prev) =>
      prev.map((row, i) => (i === storageIndex ? { ...row, [field]: value } : row))
    );
  };

  const displayTiers = useMemo(() => {
    return tierInputs
      .map((tier, storageIndex) => ({
        ...tier,
        storageIndex,
        weightNum: Number(tier.weightKg) || 0,
      }))
      .sort((a, b) => a.weightNum - b.weightNum || a.storageIndex - b.storageIndex);
  }, [tierInputs]);

  return (
    <>
      <div className="driver-frost-tier-stack">
        {displayTiers.map((tier, displayIndex) => (
          <FrosterBasePanel
            key={`tier-${tier.storageIndex}`}
            block={{ ...block, flavor: block.flavor || block.label?.split(" (")[0] }}
            positionLabel={tierPositionLabel(displayIndex)}
            weightKg={tier.weightKg}
            layers={tier.layers}
            onWeightChange={(v) => updateTier(tier.storageIndex, "weightKg", v)}
            onLayersChange={(v) => updateTier(tier.storageIndex, "layers", v)}
            showWeightInput
          />
        ))}
      </div>
      <FrostingIngredientsBar items={block.frosting_ingredients} />
      <BasicStructureBar basic={basic} />
    </>
  );
}

function SingleBaseFrosterLayout({ block }) {
  const [layers, setLayers] = useState("4");
  const [weightKg, setWeightKg] = useState(String(block.weight_kg || 0.5));
  const basic = useMemo(
    () => resolveBasicComponents(block, block.components),
    [block]
  );

  return (
    <>
      <div className="driver-frost-single-panel">
        <FrosterBasePanel
          block={block}
          positionLabel="Cake base"
          weightKg={weightKg}
          layers={layers}
          onWeightChange={setWeightKg}
          onLayersChange={setLayers}
          showWeightInput
        />
      </div>
      <FrostingIngredientsBar items={block.frosting_ingredients} />
      <BasicStructureBar basic={basic} />
    </>
  );
}

function FrostingBlockFroster({ block }) {
  if (block.kind === "cheesecake_topping") {
    return (
      <div className="driver-card driver-frost-card">
        <CheesecakeTopFillingPanel block={block} />
      </div>
    );
  }
  const isTierCake = Boolean(block.is_tier_cake || (Number(block.tiers) || 0) > 0);
  return isTierCake ? (
    <TierCakeFrosterLayout block={block} />
  ) : (
    <SingleBaseFrosterLayout block={block} />
  );
}

/* ——— Legacy layout (manager / non-driver) ——— */

function StructureTableBody({
  basic,
  scaled,
  layerComponents,
  layers,
  structureFamily,
  kind,
  scaledTotal,
  basicTotal,
  layerTotal,
}) {
  const codes = useMemo(() => orderedCodes(basic, scaled), [basic, scaled]);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[#EEEBD0]/60 text-left">
            <th className="px-2 py-2 font-semibold text-[#2D2D2D]/80">Code</th>
            <th className="px-2 py-2 font-semibold text-[#2D2D2D]/80 text-right">Basic</th>
            <th className="px-2 py-2 font-semibold text-[#2D2D2D]/80 text-right">Scaled</th>
            <th className="px-2 py-2 font-semibold text-[#EE2677] text-right">Layer</th>
            <th className="px-2 py-2 font-semibold text-[#2D2D2D]/50 text-right text-[10px]">
              Uses
            </th>
          </tr>
        </thead>
        <tbody>
          {codes.map((code) => (
            <tr key={code} className="border-t border-sky-100">
              <td className="px-2 py-2 font-bold text-[#2D2D2D]">{code}</td>
              <td className="px-2 py-2 text-right tabular-nums text-[#2D2D2D]/75">
                {basic[code] != null ? `${fmtGrams(basic[code])} g` : "—"}
              </td>
              <td className="px-2 py-2 text-right tabular-nums font-medium text-[#2D2D2D]">
                {scaled[code] != null ? `${fmtGrams(scaled[code])} g` : "—"}
              </td>
              <td className="px-2 py-2 text-right tabular-nums font-bold text-[#EE2677]">
                {layerComponents[code] != null
                  ? `${fmtGrams(layerComponents[code])} g`
                  : "—"}
              </td>
              <td className="px-2 py-2 text-right text-[10px] font-semibold text-[#888]">
                {formatUsesHint(code, layers, structureFamily, kind)}
              </td>
            </tr>
          ))}
          <tr className="border-t-2 border-sky-200 bg-sky-50/80 font-extrabold">
            <td className="px-2 py-2 text-[#2D2D2D]">Total</td>
            <td className="px-2 py-2 text-right text-[#2D2D2D]/70">
              {fmtGrams(basicTotal)} g
            </td>
            <td className="px-2 py-2 text-right text-[#2D2D2D]">{fmtGrams(scaledTotal)} g</td>
            <td className="px-2 py-2 text-right text-[#EE2677]">{fmtGrams(layerTotal)} g</td>
            <td />
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function SingleBaseStructureTable({ block }) {
  const [layers, setLayers] = useState("4");
  const scaled = block.components || {};
  const basic = useMemo(() => resolveBasicComponents(block, scaled), [block, scaled]);
  const layerComponents = useMemo(
    () =>
      computePerLayerComponents(scaled, block.structure_family, block.kind, layers),
    [scaled, block.structure_family, block.kind, layers]
  );
  const basicTotal = sumComponents(basic);
  const scaledTotal = block.total_grams ?? sumComponents(scaled);
  const layerTotal = sumComponents(layerComponents);
  const L = Math.max(1, Math.floor(Number(layers) || 1));

  return (
    <>
      <div className="border-b border-sky-100 bg-white px-3 py-3">
        <Label htmlFor={`layers-${block.label}`} className="text-xs font-bold uppercase text-[#555]">
          Base layers (L)
        </Label>
        <Input
          id={`layers-${block.label}`}
          type="number"
          min={1}
          max={20}
          step={1}
          value={layers}
          onChange={(e) => setLayers(e.target.value)}
          className="h-11 max-w-[120px] text-lg font-bold"
          inputMode="numeric"
        />
        {L < 2 ? (
          <p className="mt-2 text-[11px] text-amber-800">
            F and T need at least 2 layers (filled between layers).
          </p>
        ) : null}
      </div>
      <StructureTableBody
        basic={basic}
        scaled={scaled}
        layerComponents={layerComponents}
        layers={layers}
        structureFamily={block.structure_family}
        kind={block.kind}
        scaledTotal={scaledTotal}
        basicTotal={basicTotal}
        layerTotal={layerTotal}
      />
    </>
  );
}

function TierCakeStructureSection({ block }) {
  const tierCount = block.tier_base_count || (Number(block.tiers) || 0) + 1;
  const totalKg = Number(block.weight_kg) || 0.5;
  const suggested = useMemo(
    () => suggestTierBaseWeights(totalKg, tierCount),
    [totalKg, tierCount]
  );
  const [tierInputs, setTierInputs] = useState(() =>
    suggested.map((w) => ({ weightKg: String(w), layers: "4" }))
  );
  const updateTier = (storageIndex, field, value) => {
    setTierInputs((prev) =>
      prev.map((row, i) => (i === storageIndex ? { ...row, [field]: value } : row))
    );
  };
  const displayTiers = useMemo(() => {
    return tierInputs
      .map((tier, storageIndex) => ({
        ...tier,
        storageIndex,
        weightNum: Number(tier.weightKg) || 0,
      }))
      .sort((a, b) => a.weightNum - b.weightNum || a.storageIndex - b.storageIndex);
  }, [tierInputs]);

  return (
    <div className="space-y-3 px-3 pb-3">
      {displayTiers.map((tier, displayIndex) => (
        <LegacyTierPanel
          key={`tier-${tier.storageIndex}`}
          block={block}
          positionLabel={tierPositionLabel(displayIndex)}
          weightKg={tier.weightKg}
          layers={tier.layers}
          onWeightChange={(v) => updateTier(tier.storageIndex, "weightKg", v)}
          onLayersChange={(v) => updateTier(tier.storageIndex, "layers", v)}
        />
      ))}
    </div>
  );
}

function LegacyTierPanel({
  block,
  positionLabel,
  weightKg,
  layers,
  onWeightChange,
  onLayersChange,
}) {
  const flavor = block.flavor || block.label?.split(" (")[0] || "";
  const tierStruct = useMemo(
    () => getScaledFrostingStructureFromBlock(block, weightKg),
    [block, weightKg]
  );
  const scaled = tierStruct?.components || {};
  const basic = block.base_components || {};
  const layerComponents = useMemo(
    () =>
      computePerLayerComponents(scaled, block.structure_family, block.kind, layers),
    [scaled, block.structure_family, block.kind, layers]
  );
  const basicTotal = sumComponents(basic);
  const scaledTotal = tierStruct?.totalGrams ?? sumComponents(scaled);
  const layerTotal = sumComponents(layerComponents);

  return (
    <div className="rounded-xl border border-violet-200 bg-violet-50/40">
      <div className="border-b border-violet-100 px-3 py-2">
        <p className="text-sm font-extrabold text-violet-950">
          {positionLabel} · {weightKg} kg
        </p>
      </div>
      <div className="grid gap-3 border-b border-violet-100 bg-white px-3 py-3 sm:grid-cols-2">
        <div>
          <Label className="text-xs font-bold uppercase text-[#555]">Weight (kg)</Label>
          <Input
            type="number"
            min={0.25}
            max={20}
            step={0.25}
            value={weightKg}
            onChange={(e) => onWeightChange(e.target.value)}
            className="mt-1 h-11 text-lg font-bold"
            inputMode="decimal"
          />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase text-[#555]">Layers (L)</Label>
          <Input
            type="number"
            min={1}
            max={20}
            step={1}
            value={layers}
            onChange={(e) => onLayersChange(e.target.value)}
            className="mt-1 h-11 text-lg font-bold"
            inputMode="numeric"
          />
        </div>
      </div>
      <StructureTableBody
        basic={basic}
        scaled={scaled}
        layerComponents={layerComponents}
        layers={layers}
        structureFamily={block.structure_family}
        kind={block.kind}
        scaledTotal={scaledTotal}
        basicTotal={basicTotal}
        layerTotal={layerTotal}
      />
    </div>
  );
}

function FrostingBlockTable({ block }) {
  const isTierCake = Boolean(block.is_tier_cake || (Number(block.tiers) || 0) > 0);
  const isCheesecake = block.kind === "cheesecake_topping";
  return (
    <div className="overflow-hidden rounded-2xl border-2 border-sky-300 bg-white shadow-sm">
      <div className="border-b border-sky-200 bg-sky-50 px-3 py-2.5">
        <p className="text-sm font-bold text-[#2D2D2D]">{block.label}</p>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-[#EE2677] px-3 py-0.5 text-sm font-extrabold text-white">
            {isCheesecake ? "Cheese cake" : block.kind_label}
          </span>
          {isTierCake ? (
            <span className="rounded-full bg-violet-600 px-3 py-0.5 text-sm font-extrabold text-white">
              {Number(block.tiers) || 0} {Number(block.tiers) === 1 ? "tier" : "tiers"}
            </span>
          ) : null}
        </div>
      </div>
      {isCheesecake ? (
        <CheesecakeTopFillingPanel block={block} />
      ) : isTierCake ? (
        <TierCakeStructureSection block={block} />
      ) : (
        <SingleBaseStructureTable block={block} />
      )}
    </div>
  );
}

/** Standalone froster calculator — add/remove as many cake bases as needed. */
export function FrosterCalculatorBases({ block }) {
  const nextId = useRef(2);
  const [bases, setBases] = useState([{ id: 1, weightKg: "0.5", layers: "4" }]);

  const basic = useMemo(
    () => resolveBasicComponents(block, block?.components),
    [block]
  );

  const addBase = () => {
    setBases((prev) => [
      ...prev,
      { id: nextId.current++, weightKg: "0.5", layers: "4" },
    ]);
  };

  const removeBase = (id) => {
    setBases((prev) => (prev.length <= 1 ? prev : prev.filter((b) => b.id !== id)));
  };

  const updateBase = (id, field, value) => {
    setBases((prev) => prev.map((b) => (b.id === id ? { ...b, [field]: value } : b)));
  };

  const displayBases = useMemo(() => {
    return [...bases]
      .map((base) => ({
        ...base,
        weightNum: Number(base.weightKg) || 0,
      }))
      .sort((a, b) => a.weightNum - b.weightNum || a.id - b.id);
  }, [bases]);

  if (!block) return null;

  return (
    <div className="driver-card driver-frost-card">
      <div className="driver-frost-tier-stack">
        {displayBases.map((base, displayIndex) => (
          <FrosterBasePanel
            key={base.id}
            block={{ ...block, flavor: block.flavor || block.label?.split(" (")[0] }}
            positionLabel={`Base ${displayIndex + 1}`}
            weightKg={base.weightKg}
            layers={base.layers}
            onWeightChange={(v) => updateBase(base.id, "weightKg", v)}
            onLayersChange={(v) => updateBase(base.id, "layers", v)}
            showWeightInput
            onRemove={bases.length > 1 ? () => removeBase(base.id) : undefined}
            weightMin={0.25}
            weightStep={0.5}
            weightDecimals={2}
            calculatorWeight
          />
        ))}
      </div>
      <button type="button" className="driver-calc-add-base" onClick={addBase}>
        + Add base
      </button>
      <FrostingIngredientsBar items={block.frosting_ingredients} />
      <BasicStructureBar basic={basic} />
    </div>
  );
}

/** Scaled frosting structure on froster task link (RTG or Custom from order type). */
export default function FrosterStructureTable({
  blocks,
  orderType,
  tiers: orderTiers,
  weightKg,
  design = "legacy",
}) {
  const driver = design === "driver";

  if (!blocks?.length) {
    if (!orderType) return null;
    if (driver) {
      return (
        <div className="driver-card" style={{ fontSize: 14, color: "#92400E" }}>
          No frosting structure for &ldquo;{orderType}&rdquo; (Dry Cake / Pastry). Cheesecakes use top-filling lines on the froster sheet.
        </div>
      );
    }
    return (
      <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900">
        No frosting structure for order type &ldquo;{orderType}&rdquo; (Dry Cake / Pastry). Cheesecakes use top-filling lines.
      </div>
    );
  }

  const enrichedBlocks = blocks.map((block) => ({
    ...block,
    tiers: block.tiers ?? orderTiers ?? 0,
    is_tier_cake: block.is_tier_cake ?? (Number(block.tiers ?? orderTiers) || 0) > 0,
    tier_base_count:
      block.tier_base_count ??
      ((Number(block.tiers ?? orderTiers) || 0) > 0
        ? Number(block.tiers ?? orderTiers) + 1
        : 1),
    weight_kg: block.weight_kg ?? weightKg,
  }));

  if (driver) {
    return (
      <div className="driver-card driver-frost-card">
        {enrichedBlocks.map((block, idx) => (
          <FrostingBlockFroster key={`${block.label}-${idx}`} block={block} />
        ))}
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-3">
      <div className="rounded-2xl border-2 border-[#EE2677]/30 bg-gradient-to-b from-white to-[#FFF7FA] px-3 py-2">
        <p className="text-base font-extrabold uppercase tracking-wide text-[#2D2D2D]">
          {enrichedBlocks.some((b) => b.kind === "cheesecake_topping")
            ? "Top filling"
            : "Frosting structure"}
        </p>
      </div>
      {enrichedBlocks.map((block, idx) => (
        <FrostingBlockTable key={`${block.label}-${idx}`} block={block} />
      ))}
    </div>
  );
}
